import { FC, memo, PropsWithChildren, ReactNode } from "react";

import {
  Ellipsis as EllipsisIcon,
  FilledHexagon01 as HexagonIcon,
  FilledHexagonGrande as BigHexagonIcon,
  FilledPentagon as PentagonIcon,
  FilledSquare as SquareIcon,
  FilledTriangle as TriangleIcon,
  RightCaret as RightCaretIcon,
  BigCodeBrowser as CodeBrowserIcon,
} from "@hl/base-components/lib/assets/icons.generated";
import { BACKGROUND_COLOR } from "@hl/base-components/lib/theme/colors";
import {
  FEATURE_FLAGS,
  useFeatureFlags,
} from "@hl/shared-features/lib/features/auth/hooks/useFeatureFlags";
import {
  Box,
  Card,
  Center,
  Container,
  Group,
  Space,
  Stack,
  Text,
  Title,
  UnstyledButton,
} from "@mantine/core";
import { Link } from "react-router-dom";

import { _CollectionType } from "apollo/graphql.generated";

type Handler =
  | { type: "button"; onClick(): void }
  | { type: "link"; to: string };
export interface CollectionTypeHandler {
  type: _CollectionType;
  handler: Handler;
}

interface _CollectionTypeOptionProps {
  label: string;
  description: string;
  handler: Handler;
  leftIcon?: ReactNode;
  border?: boolean;
}

const CollectionTypeWrapper = ({
  handler,
  children,
}: {
  handler: Handler;
  children: ReactNode;
}) => {
  if (handler.type === "button") {
    return (
      <UnstyledButton onClick={handler.onClick}>{children}</UnstyledButton>
    );
  }

  return (
    <Link style={{ textDecoration: "none" }} to={handler.to}>
      {children}
    </Link>
  );
};

const _CollectionTypeOption = ({
  label,
  description,
  handler,
  leftIcon = null,
  border,
}: _CollectionTypeOptionProps) => {
  return (
    <CollectionTypeWrapper handler={handler}>
      <Card
        radius={22}
        w={728}
        withBorder={!!border}
        bg={BACKGROUND_COLOR.PRIMARY}
      >
        <Group noWrap position="apart">
          <Group spacing={18} noWrap>
            <Box
              sx={() => ({
                minWidth: "146px",
              })}
            >
              {leftIcon}
            </Box>
            <Stack spacing={4}>
              <Title order={6} fw={500}>
                {label}
              </Title>
              <Text size="sm">
                <Group spacing={4}>{description}</Group>
              </Text>
            </Stack>
          </Group>
          <Stack align="center">
            <RightCaretIcon />
          </Stack>
        </Group>
      </Card>
    </CollectionTypeWrapper>
  );
};

type IconWrapperProps = {
  corner: "bottom-left" | "bottom-right" | "upper-left" | "upper-right" | "all";
  width?: string;
  height?: string;
};
/**
 * Wraps a SVG Icon to add a grey background, with a rounded corner
 * props let code create a 2x2 grid with different rounded corners
 */
const IconWrapper: FC<PropsWithChildren<IconWrapperProps>> = ({
  corner,
  width,
  height,
  children,
}) => {
  /* eslint-disable  @typescript-eslint/no-explicit-any */
  const roundedCorner = (theme: any) => {
    if (corner === "bottom-left") return `0 0 0 ${theme.radius.md}px`;
    if (corner === "bottom-right") return `0 0 ${theme.radius.md}px 0`;
    if (corner === "upper-left") return `${theme.radius.md}px 0 0 0`;
    if (corner === "upper-right") return `0 ${theme.radius.md}px 0 0`;
    if (corner === "all") return theme.radius.md;
    return "0";
  };
  return (
    <Box
      sx={(theme) => ({
        backgroundColor: theme.colors.codeSnippetContainerBackground[0],
        borderRadius: roundedCorner(theme),
        cursor: "pointer",
        width: width ?? "71px",
        height: height ?? "71px",
        alignContent: "center",
        justifyContent: "center",
      })}
    >
      <Center>
        <Box sx={() => ({ marginTop: "18px" })}>{children}</Box>
      </Center>
    </Box>
  );
};

const SeriesIcon = () => (
  <Group position={"left"} spacing={4}>
    <Container m={0} p={0}>
      <IconWrapper corner={"upper-left"}>
        <TriangleIcon />
      </IconWrapper>
      <Box sx={() => ({ marginTop: "4px" })}>
        <IconWrapper corner={"bottom-left"}>
          <PentagonIcon />
        </IconWrapper>
      </Box>
    </Container>
    <Container m={0} p={0}>
      <IconWrapper corner={"upper-right"}>
        <SquareIcon />
      </IconWrapper>
      <Box sx={() => ({ marginTop: "4px" })}>
        <IconWrapper corner={"bottom-right"}>
          <HexagonIcon />
        </IconWrapper>
      </Box>
    </Container>
  </Group>
);
const LimitedEditionIcon = () => (
  <Group position={"left"} spacing={4}>
    <Container m={0} p={0}>
      <IconWrapper corner={"upper-left"}>
        <TriangleIcon />
      </IconWrapper>
      <Box sx={() => ({ marginTop: "4px" })}>
        <IconWrapper corner={"bottom-left"}>
          <TriangleIcon />
        </IconWrapper>
      </Box>
    </Container>
    <Container m={0} p={0}>
      <IconWrapper corner={"upper-right"}>
        <TriangleIcon />
      </IconWrapper>
      <Box sx={() => ({ marginTop: "4px" })}>
        <IconWrapper corner={"bottom-right"}>
          <TriangleIcon />
        </IconWrapper>
      </Box>
    </Container>
  </Group>
);

const OpenEditionIcon = () => (
  <Group position={"left"} spacing={4}>
    <Container m={0} p={0}>
      <IconWrapper corner={"upper-left"}>
        <SquareIcon />
      </IconWrapper>
      <Box sx={() => ({ marginTop: "4px" })}>
        <IconWrapper corner={"bottom-left"}>
          <SquareIcon />
        </IconWrapper>
      </Box>
    </Container>
    <Container m={0} p={0}>
      <IconWrapper corner={"upper-right"}>
        <SquareIcon />
      </IconWrapper>
      <Box sx={() => ({ marginTop: "4px" })}>
        <IconWrapper corner={"bottom-right"}>
          <EllipsisIcon />
        </IconWrapper>
      </Box>
    </Container>
  </Group>
);

const AuctionIcon = () => (
  <IconWrapper corner={"all"} width={"144px"} height={"120px"}>
    <Box sx={() => ({ marginTop: "10px" })}>
      <BigHexagonIcon />
    </Box>
  </IconWrapper>
);

const CodeGenSeriesIcon = () => (
  <IconWrapper corner={"all"} width={"144px"} height={"120px"}>
    <Box sx={() => ({ marginTop: "10px" })}>
      <CodeBrowserIcon />
    </Box>
  </IconWrapper>
);

const SelectCollectionCards = ({
  title,
  withBorder,
  handlers,
}: {
  title: ReactNode;
  handlers: CollectionTypeHandler[];
  withBorder?: boolean;
}) => {
  const enableGenSeries = useFeatureFlags(FEATURE_FLAGS.ENABLE_JOBS_GEN_SERIES);

  const collectionTypes = [
    {
      label: "Series",
      description:
        "A collection of NFTs in which the artwork and metadata for each token can be unique.",
      leftIcon: <SeriesIcon />,
      type: _CollectionType.Series,
    },
    {
      label: "Open Edition",
      description:
        "An open-ended collection of NFTs in which the artwork and metadata for each token is the same.",
      leftIcon: <OpenEditionIcon />,
      type: _CollectionType.OpenEdition,
    },
    {
      label: "Limited Edition",
      description:
        "A limited collection of NFTs in which the artwork and metadata for each token is the same.",
      leftIcon: <LimitedEditionIcon />,
      type: _CollectionType.LimitedEdition,
    },
    {
      label: "One of One",
      description:
        "A collection containing a single, unique NFT with its own artwork and metadata.",
      leftIcon: <AuctionIcon />,
      type: _CollectionType.OneOfOne,
    },
  ];

  if (enableGenSeries) {
    collectionTypes.unshift({
      label: "Generative Series",
      description:
        "A collection of NFTs in which the artwork and metadata for each token is generated by code when the token is minted.",
      leftIcon: <CodeGenSeriesIcon />,
      type: _CollectionType.GenerativeSeries,
    });
  }

  return (
    <div>
      {title}
      <Space h={48} />
      <Stack align="center">
        {handlers.map((handler, i) => {
          const collectionType = collectionTypes.find(
            (ct) => ct.type === handler.type
          );
          if (!collectionType) return null;
          return (
            <_CollectionTypeOption
              key={i}
              handler={handler.handler}
              border={withBorder}
              {...collectionType}
            />
          );
        })}
      </Stack>
    </div>
  );
};

export default memo(SelectCollectionCards);
